import React, { useState, useCallback, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    LinearProgress,
    Alert,
    IconButton,
    Paper,
    Tooltip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { UploadDialogProps, AssetType } from '../AssetExplorer.types';
import { fileToBase64, formatFileSize, getAssetTypeFromMime, fileQualifiesForLocalImagePreview } from '../AssetExplorer.api';
import { useAssetExplorerApi } from '../AssetExplorerConfig';
import {
    assetExplorerUploadThumbnailBg,
    assetExplorerUploadTypeIconColor,
} from '../assetExplorerTheme';

interface FileToUpload {
    file: File;
    name: string;
    type: AssetType;
    preview?: string;
}

/** Browser-previewable image thumbnail, else VIDEO/AUDIO glyphs or generic file icon */
const UploadFileThumbnail: React.FC<{ fileItem: FileToUpload }> = ({ fileItem }) => {
    const theme = useTheme();
    const [imgFailed, setImgFailed] = useState(false);

    useEffect(() => {
        setImgFailed(false);
    }, [fileItem.preview, fileItem.type]);

    const iconColor = assetExplorerUploadTypeIconColor(theme);
    const placeholderIcon =
        fileItem.type === 'VIDEO' ? (
            <VideoFileIcon sx={{ fontSize: 28, color: iconColor }} />
        ) : fileItem.type === 'AUDIO' ? (
            <AudioFileIcon sx={{ fontSize: 28, color: iconColor }} />
        ) : (
            <InsertDriveFileIcon sx={{ fontSize: 28, color: iconColor }} />
        );

    return (
        <Box
            sx={{
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: assetExplorerUploadThumbnailBg(theme),
                borderRadius: 1,
                flexShrink: 0,
                overflow: 'hidden',
            }}
        >
            {fileItem.preview && !imgFailed ? (
                <Box
                    component="img"
                    src={fileItem.preview}
                    alt=""
                    onError={() => setImgFailed(true)}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1 }}
                />
            ) : (
                placeholderIcon
            )}
        </Box>
    );
};

export const UploadDialog: React.FC<UploadDialogProps> = ({
    open,
    folderId,
    onClose,
    onUploadComplete,
}) => {
    const { uploadAsset } = useAssetExplorerApi();
    const theme = useTheme();
    const [files, setFiles] = useState<FileToUpload[]>([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [currentUpload, setCurrentUpload] = useState<string>('');

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        const droppedFiles = Array.from(e.dataTransfer.files);
        addFiles(droppedFiles);
    }, []);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const selectedFiles = Array.from(e.target.files);
            addFiles(selectedFiles);
        }
    };

    const addFiles = (newFiles: File[]) => {
        const filesToAdd: FileToUpload[] = newFiles.map((file) => {
            const detectedType = getAssetTypeFromMime(file.type) as AssetType;
            return {
                file,
                name: file.name.replace(/\.[^/.]+$/, ''), // Remove extension for display name
                type: detectedType,
                preview: fileQualifiesForLocalImagePreview(file) ? URL.createObjectURL(file) : undefined,
            };
        });
        setFiles((prev) => [...prev, ...filesToAdd]);
        setError(null);
    };

    const removeFile = (index: number) => {
        setFiles((prev) => {
            const newFiles = [...prev];
            if (newFiles[index].preview) {
                URL.revokeObjectURL(newFiles[index].preview!);
            }
            newFiles.splice(index, 1);
            return newFiles;
        });
    };

    const updateFileName = (index: number, name: string) => {
        setFiles((prev) => {
            const newFiles = [...prev];
            newFiles[index] = { ...newFiles[index], name };
            return newFiles;
        });
    };

    const updateFileType = (index: number, type: AssetType) => {
        setFiles((prev) => {
            const newFiles = [...prev];
            newFiles[index] = { ...newFiles[index], type };
            return newFiles;
        });
    };

    const handleUpload = async () => {
        if (files.length === 0) return;

        setUploading(true);
        setError(null);
        setUploadProgress(0);

        try {
            for (let i = 0; i < files.length; i++) {
                const fileToUpload = files[i];
                setCurrentUpload(fileToUpload.name);
                setUploadProgress(Math.round((i / files.length) * 100));

                const base64Data = await fileToBase64(fileToUpload.file);

                await uploadAsset({
                    folderId,
                    name: fileToUpload.name,
                    type: fileToUpload.type,
                    assetData: base64Data,
                    mimeType: fileToUpload.file.type,
                    originalName: fileToUpload.file.name,
                    size: fileToUpload.file.size,
                });
            }

            setUploadProgress(100);
            setFiles([]);
            onUploadComplete();
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to upload files');
        } finally {
            setUploading(false);
            setCurrentUpload('');
        }
    };

    const handleClose = () => {
        if (uploading) return;
        // Cleanup previews
        files.forEach((f) => {
            if (f.preview) URL.revokeObjectURL(f.preview);
        });
        setFiles([]);
        setError(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 },
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Upload Assets
                </Typography>
                <IconButton
                    onClick={handleClose}
                    disabled={uploading}
                    color="secondary"
                    size="small"
                    sx={{
                        border: '1px solid',
                        borderColor: 'secondary.main',
                        '&:hover': {
                            backgroundColor: 'action.hover',
                            borderColor: 'secondary.dark',
                        },
                    }}
                    aria-label="Close"
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Drop Zone */}
                <Paper
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                    sx={{
                        p: 4,
                        mb: 3,
                        textAlign: 'center',
                        border: `2px dashed ${theme.palette.divider}`,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.secondary.main, 0.04),
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            borderColor: theme.palette.secondary.main,
                            backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                        },
                    }}
                    onClick={() => document.getElementById('file-upload-input')?.click()}
                >
                    <input
                        id="file-upload-input"
                        type="file"
                        multiple
                        hidden
                        onChange={handleFileSelect}
                        accept="image/*,video/*,audio/*,.zip,.pdf,.doc,.docx,.xls,.xlsx,.txt,.heic,.heif"
                    />
                    <CloudUploadIcon sx={{ fontSize: 48, color: theme.palette.secondary.main, mb: 1 }} />
                    <Typography variant="h6" color="textSecondary" sx={{ mb: 0.5 }}>
                        Drag and drop files here
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        or click to browse
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                        Supported: Images, Videos, Audio, ZIP, PDF, Documents
                    </Typography>
                </Paper>

                {/* Files List */}
                {files.length > 0 && (
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Files to Upload ({files.length})
                        </Typography>
                        {files.map((fileItem, index) => (
                            <Paper
                                key={index}
                                sx={{
                                    p: 2,
                                    mb: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <UploadFileThumbnail fileItem={fileItem} />

                                {/* Name Input */}
                                <TextField
                                    label="Asset Name"
                                    value={fileItem.name}
                                    onChange={(e) => updateFileName(index, e.target.value)}
                                    size="small"
                                    sx={{ flex: 1 }}
                                    disabled={uploading}
                                />

                                {/* Type Select */}
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel>Type</InputLabel>
                                    <Select
                                        value={fileItem.type}
                                        label="Type"
                                        onChange={(e) => updateFileType(index, e.target.value as AssetType)}
                                        disabled={uploading}
                                    >
                                        <MenuItem value="IMAGE">Image</MenuItem>
                                        <MenuItem value="VIDEO">Video</MenuItem>
                                        <MenuItem value="AUDIO">Audio</MenuItem>
                                        <MenuItem value="ZIP">ZIP</MenuItem>
                                        <MenuItem value="DOCUMENT">Document</MenuItem>
                                        <MenuItem value="OTHER">Other</MenuItem>
                                    </Select>
                                </FormControl>

                                {/* Size */}
                                <Typography variant="caption" color="textSecondary" sx={{ minWidth: 70 }}>
                                    {formatFileSize(fileItem.file.size)}
                                </Typography>

                                <Tooltip title="Remove from list">
                                    <span>
                                        <Button
                                            color="error"
                                            variant="contained"
                                            size="small"
                                            onClick={() => removeFile(index)}
                                            disabled={uploading}
                                            aria-label="Remove file"
                                            sx={{
                                                borderRadius: 1,
                                                textTransform: 'none',
                                                px: 1.25,
                                                minWidth: 36,
                                                minHeight: 36,
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Paper>
                        ))}
                    </Box>
                )}

                {/* Upload Progress */}
                {uploading && (
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" color="textSecondary">
                                Uploading: {currentUpload}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {uploadProgress}%
                            </Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={uploadProgress} color="secondary" />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button
                    onClick={handleClose}
                    disabled={uploading}
                    variant="outlined"
                    color="secondary"
                    sx={{ borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleUpload}
                    disabled={uploading || files.length === 0}
                    startIcon={<CloudUploadIcon />}
                >
                    {uploading ? 'Uploading...' : `Upload ${files.length} File${files.length !== 1 ? 's' : ''}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UploadDialog;
